// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  createMsalConfig,
  createTokenRequest,
  allowsUnresolvedTenant,
  getConfiguredAuthorityTenantId,
  getExpectedTokenIssuers,
} from "../authConfig";

export class WrongAppAccessTokenError extends Error {
  constructor(message = "Access token is not for this application.") {
    super(message);
    this.name = "WrongAppAccessTokenError";
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2 || parts[1] === undefined || parts[1].length === 0) {
    return null;
  }
  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export const CLOCK_SKEW_SECONDS = 300;

function now(): number {
  return Date.now();
}

function readNumericClaim(payload: Record<string, unknown>, claim: string): number | undefined {
  const value = payload[claim];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readStringClaim(payload: Record<string, unknown>, claim: string): string | undefined {
  const value = payload[claim];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function normalizeAppId(value: string): string {
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith("api://") ? trimmed.slice("api://".length).split("/")[0]! : trimmed;
}

export function getConfiguredSpaClientId(): string | undefined {
  const clientId = createMsalConfig().auth.clientId.trim();
  return clientId.length > 0 ? clientId.toLowerCase() : undefined;
}

export function getConfiguredApiAudienceIds(): string[] {
  const clientId = getConfiguredSpaClientId();
  const fromScopes = createTokenRequest()
    .scopes
    .map((scope) => scope.trim())
    .filter((scope) => scope.startsWith("api://"))
    .map((scope) => normalizeAppId(scope));

  const ids = new Set<string>(fromScopes);
  if (clientId !== undefined) {
    ids.add(clientId);
  }
  return [...ids];
}

export function readAccessTokenActorId(token: string): string | undefined {
  const payload = decodeJwtPayload(token);
  if (payload === null) {
    return undefined;
  }

  return readStringClaim(payload, "oid") ?? readStringClaim(payload, "sub");
}

export function assertAccessTokenForConfiguredApp(token: string): void {
  const clientId = getConfiguredSpaClientId();
  if (clientId === undefined) {
    throw new WrongAppAccessTokenError("Entra client id is not configured.");
  }

  const payload = decodeJwtPayload(token);
  if (payload === null) {
    throw new WrongAppAccessTokenError("Access token is malformed.");
  }

  const audRaw = payload.aud;
  const audiences = (Array.isArray(audRaw) ? audRaw : [audRaw])
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .map(normalizeAppId);
  const allowedAudiences = getConfiguredApiAudienceIds();
  if (
    audiences.length === 0 ||
    !audiences.some((audience) => allowedAudiences.includes(audience))
  ) {
    throw new WrongAppAccessTokenError("Access token audience is not for this application.");
  }

  const authorizedParty =
    readStringClaim(payload, "azp") ?? readStringClaim(payload, "appid");
  if (authorizedParty === undefined || normalizeAppId(authorizedParty) !== clientId) {
    throw new WrongAppAccessTokenError("Access token was issued for a different client.");
  }

  const configuredTenant = getConfiguredAuthorityTenantId();
  if (configuredTenant === undefined) {
    if (!allowsUnresolvedTenant()) {
      throw new WrongAppAccessTokenError("No tenant is configured for this environment.");
    }
  } else {
    const tokenTenant = readStringClaim(payload, "tid");
    if (tokenTenant === undefined || tokenTenant.toLowerCase() !== configuredTenant) {
      throw new WrongAppAccessTokenError("Access token tenant does not match this environment.");
    }
    const issuer = readStringClaim(payload, "iss");
    if (issuer === undefined || !getExpectedTokenIssuers().includes(issuer)) {
      throw new WrongAppAccessTokenError("Access token issuer does not match this environment.");
    }
  }

  const nowSeconds = Math.floor(now() / 1000);
  const expiresAt = readNumericClaim(payload, "exp");
  if (expiresAt === undefined || expiresAt + CLOCK_SKEW_SECONDS < nowSeconds) {
    throw new WrongAppAccessTokenError("Access token has expired or has no expiry.");
  }
  const notBefore = readNumericClaim(payload, "nbf");
  if (notBefore !== undefined && notBefore - CLOCK_SKEW_SECONDS > nowSeconds) {
    throw new WrongAppAccessTokenError("Access token is not valid yet.");
  }
}
